// Phone-frame live preview of the in-progress AppConfig. Renders a vertical
// device shell with the configured identity (logo + name), a simulated
// dashboard area, and the configured bottom navigation at the foot.
//
// This is intentionally NOT a real React Native render — that would require
// react-native-web and is overkill for the Builder. We render an HTML/CSS
// approximation that nails the visual layout: tabs, icons, FAB, colors.

import React from 'react';
import * as Icons from '@/components/icons';
import type { AppConfig, BottomNavItem } from '@erp/app-builder';
import { getScreen } from '@erp/app-builder';

// Map kebab-case icon names from app-builder config to the project's
// curated icon set (iconsax aliased to English names in @/components/icons).
// Unknown names render an empty box so the layout stays stable.
const ICON_MAP: Record<string, React.ComponentType<{ size?: number; color?: string }>> = {
  home: Icons.LayoutDashboard,
  receipt: Icons.Receipt,
  'shopping-cart': Icons.ShoppingCart,
  'shopping-bag': Icons.ShoppingBag,
  users: Icons.Users,
  user: Icons.User,
  'user-plus': Icons.UserPlus,
  menu: Icons.Menu,
  search: Icons.Search,
  bell: Icons.Bell,
  package: Icons.Package,
  'file-text': Icons.FileText,
  'clipboard-list': Icons.ClipboardList,
  truck: Icons.Truck,
  warehouse: Icons.Warehouse,
  landmark: Icons.Landmark,
  banknote: Icons.Banknote,
  'book-check': Icons.BookCheck,
  settings: Icons.Settings,
  'sliders-horizontal': Icons.SlidersHorizontal,
  plus: Icons.Plus,
  'scan-barcode': Icons.ScanBarcode,
  'arrow-left-right': Icons.ArrowRightLeft,
  'rotate-ccw': Icons.RotateCcw,
  'alert-circle': Icons.AlertTriangle,
  globe: Icons.LayoutDashboard,
  monitor: Icons.LayoutDashboard,
  briefcase: Icons.User,
  printer: Icons.Printer,
  building: Icons.Building2,
  palette: Icons.Colorfilter,
  languages: Icons.HelpCircle,
};

interface MobilePreviewProps {
  config: AppConfig;
  /** Active screen id to highlight (if undefined, uses bottomNav[0].destination). */
  activeScreenId?: string;
  /** Currently picked language for label rendering. */
  lang?: 'fr' | 'ar' | 'en';
}

export function MobilePreview({ config, activeScreenId, lang = 'fr' }: MobilePreviewProps) {
  const primary = config.theme.primaryColor;
  const active = activeScreenId ?? config.bottomNav[0]?.destination;

  return (
    <div
      className="relative mx-auto"
      style={{
        width: 320,
        height: 660,
        background: '#000',
        borderRadius: 44,
        padding: 8,
        boxShadow: '0 30px 60px -20px rgba(0,0,0,0.4)',
      }}
    >
      {/* Notch */}
      <div
        className="absolute top-2 left-1/2 -translate-x-1/2 z-10"
        style={{ width: 110, height: 24, background: '#000', borderRadius: 12 }}
      />
      {/* Inner screen */}
      <div
        className="relative h-full w-full overflow-hidden flex flex-col"
        style={{
          borderRadius: 36,
          background: '#fff',
        }}
      >
        {/* Status bar */}
        <div
          className="flex items-center justify-between px-6 pt-3 pb-2 text-[10px] font-medium"
          style={{ color: '#000' }}
        >
          <span>9:41</span>
          <div className="flex items-center gap-1">
            <span>●●●</span>
          </div>
        </div>

        {/* Header */}
        <div className="px-4 pt-2 pb-3 flex items-center gap-3">
          <div
            className="rounded-2xl flex items-center justify-center text-white font-bold"
            style={{ width: 36, height: 36, background: primary }}
          >
            {config.identity.shortName.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-gray-900 truncate">
              {config.identity.name[lang] ?? config.identity.name.fr}
            </div>
            {config.identity.tagline ? (
              <div className="text-[10px] text-gray-500 truncate">
                {(config.identity.tagline as Record<string, string>)[lang] ?? config.identity.tagline.fr}
              </div>
            ) : null}
          </div>
        </div>

        {/* Body — placeholder content for the active screen */}
        <div className="flex-1 px-4 pb-2 overflow-hidden">
          <PreviewBody config={config} active={active} lang={lang} />
        </div>

        {/* Bottom nav */}
        <BottomNav config={config} primary={primary} active={active} lang={lang} />
      </div>
    </div>
  );
}

function PreviewBody({
  config,
  active,
  lang,
}: {
  config: AppConfig;
  active: string | undefined;
  lang: 'fr' | 'ar' | 'en';
}) {
  const screen = active ? getScreen(active) : undefined;

  if (screen?.view === 'dashboard') {
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          {config.shortcuts.slice(0, 4).map((s) => (
            <div key={s.id} className="rounded-xl border border-gray-200 p-2 flex items-center gap-2">
              <DynamicIcon name={s.icon} size={14} color={config.theme.primaryColor} />
              <span className="text-[10px] text-gray-700">{s.labelKey.split('.').pop()}</span>
            </div>
          ))}
        </div>
        <div className="rounded-xl border border-gray-200 p-3">
          <div className="text-[10px] text-gray-500">Factures (CA)</div>
          <div className="text-base font-bold text-gray-900">12 450,000 DT</div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-xl border border-gray-200 p-3">
            <div className="text-[10px] text-gray-500">Impayées</div>
            <div className="text-base font-bold text-orange-600">7</div>
          </div>
          <div className="rounded-xl border border-gray-200 p-3">
            <div className="text-[10px] text-gray-500">Clients</div>
            <div className="text-base font-bold text-gray-900">142</div>
          </div>
        </div>
      </div>
    );
  }

  if (screen?.view === 'list') {
    return (
      <div className="space-y-2">
        <div className="rounded-lg border border-gray-200 px-3 py-2 text-[10px] text-gray-400">
          Rechercher…
        </div>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-xl border border-gray-200 p-2 flex items-center gap-2">
            <div
              className="rounded-full w-7 h-7 flex items-center justify-center text-[10px] font-bold"
              style={{ background: config.theme.primaryColor + '20', color: config.theme.primaryColor }}
            >
              {String.fromCharCode(65 + i)}
            </div>
            <div className="flex-1">
              <div className="text-xs font-semibold text-gray-900">Élément {i + 1}</div>
              <div className="text-[10px] text-gray-500">Sous-titre</div>
            </div>
            <span className="text-gray-400 text-xs">›</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-full text-[10px] text-gray-400">
      {screen ? screen.view : 'Aperçu'}
    </div>
  );
}

function BottomNav({
  config,
  primary,
  active,
  lang,
}: {
  config: AppConfig;
  primary: string;
  active: string | undefined;
  lang: 'fr' | 'ar' | 'en';
}) {
  return (
    <div className="border-t border-gray-200 px-2 pt-2 pb-3 flex items-end gap-1 bg-white">
      {config.bottomNav.map((item) => (
        <NavItem key={item.id} item={item} primary={primary} isActive={item.destination === active} lang={lang} />
      ))}
    </div>
  );
}

function NavItem({
  item,
  primary,
  isActive,
  lang,
}: {
  item: BottomNavItem;
  primary: string;
  isActive: boolean;
  lang: 'fr' | 'ar' | 'en';
}) {
  const tint = isActive ? primary : '#71717A';
  const label = item.label[lang] ?? item.label.fr;

  if (item.isFab) {
    return (
      <div className="flex-1 flex flex-col items-center -mt-4 gap-0.5">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
          style={{ background: primary }}
        >
          <DynamicIcon name={item.icon} size={20} color="#fff" />
        </div>
        <span className="text-[9px] font-medium" style={{ color: tint }}>{label}</span>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col items-center gap-0.5 py-1">
      <DynamicIcon name={item.icon} size={18} color={tint} />
      <span className="text-[9px] font-medium truncate max-w-full" style={{ color: tint }}>{label}</span>
    </div>
  );
}

function DynamicIcon({ name, size, color }: { name: string; size: number; color: string }) {
  const Component = ICON_MAP[name];
  if (!Component) {
    return (
      <div style={{ width: size, height: size, borderRadius: 4, background: color + '40' }} />
    );
  }
  return <Component size={size} color={color} />;
}
